<template>
  <v-dialog
    v-if="editedPost"
    :value="dialog"
    content-class="p-post-edit-dialog"
    fullscreen
    persistent
    :retain-focus="false"
    @keydown.esc="onEsc"
  >
    <v-toolbar
      color="primaryBgr"
      dark
    >
      <p-btn-icon type="close" btn-class="mr-4" tabindex="3" @click="onCloseClick"></p-btn-icon>
      <v-toolbar-title>
        <template v-if="post && post.id">{{ $t('socialMediaPost.editPost') }}</template>
        <template v-else>{{ $t('socialMediaPost.createPost') }}</template>
      </v-toolbar-title>
    </v-toolbar>

    <v-card>
      <v-form ref="form" @submit.stop.prevent>
        <div class="p-dialog-full-screen-container">
          <v-row class="mt-0">
            <v-col cols="12" lg="5">
              <v-text-field
                v-model.trim="editedPost.name"
                :rules="rules.name"
                :label="$t('socialMediaPost.edit.name')"
                :hint="$t('socialMediaPost.edit.internalUse')"
                class="p-input-lg"
                tabindex="1"
                persistent-hint
                clearable
                autofocus
              ></v-text-field>
              <p-social-media-channel-selector
                v-model="selectedChannels"
                :label="$t('socialMediaPost.edit.channels')"
                :social-medias="socialMedias"
                :social-media-channels="socialMediaChannels.sort((a, b) => a.id > b.id ? 1: -1)"
                class="mt-4"
                @add="onAddSocialMediaChannel"
              ></p-social-media-channel-selector>

              <v-text-field
                v-model.trim="editedPost.title"
                :label="$t('socialMediaPost.edit.title')"
                tabindex="1"
                clearable
              ></v-text-field>

<!--              <p-wysiwyg-editor-->
<!--                v-model="editedPost.message"-->
<!--                :label="$t('socialMediaPost.edit.message')"-->
<!--                tabindex="1"-->
<!--              ></p-wysiwyg-editor>-->
              <PSyncfusionRichTextEditor
                v-model="editedPost.message"
                :label="$t('socialMediaPost.edit.message')"
                tabindex="1"/>

              <p-file-input
                ref="fileInput"
                v-model="editedPost.images"
                :label="$t('socialMediaPost.edit.images')"
                accept="image/*"
                primary-enabled
                :primary-text="$t('socialMediaPost.edit.featured')"
                show-reorder-hint
                library-enabled
                :library-file-type="fileType.socialMedia"
                :is-marketing="true"
                multiple
                clearable
              ></p-file-input>

              <p-file-preview-area
                v-if="dialogOpened"
                ref="filePreviewArea"
                :files.sync="editedPost.images"
                :is-main="true"
              >
              </p-file-preview-area>

              <v-textarea
                v-model.trim="editedPost.embedMedia"
                :label="$t('socialMediaPost.edit.embedMedia')"
                tabindex="1"
                rows="1"
                auto-grow
              ></v-textarea>

              <v-checkbox
                v-model="editedPost.includeEvents"
                :label="$t('socialMediaPost.edit.includeEvents')"
                :hint="editedPost.includeEvents ? $t('socialMediaPost.edit.includeEvents_hint') : ''"
                persistent-hint
              ></v-checkbox>
              <included-events-list v-if="editedPost.includeEvents" />

              <v-checkbox
                v-model="editedPost.featured"
                :label="$t('socialMediaPost.edit.featuredPost')"
              ></v-checkbox>

              <v-row>
                <v-col cols="12" md="6">
                  <p-date-time-picker
                    v-model="editedPost.scheduledPublishTimeUtc"
                    :label="$t('socialMediaPost.edit.scheduledPublishTime')"
                    tabindex="1"
                    clearable
                  ></p-date-time-picker>
                </v-col>
              </v-row>

              <v-row>
                <v-col cols="12" md="6">
                  <p-date-time-picker
                    v-model="editedPost.unPublishTimeUtc"
                    :rules="rules.unPublishTimeUtc"
                    :label="$t('socialMediaPost.edit.unPublishTime')"
                    :min="editedPost.scheduledPublishTimeUtc"
                    tabindex="1"
                    clearable
                  ></p-date-time-picker>
                </v-col>
              </v-row>
            </v-col>

            <v-col cols="12" lg="7">
              <custom-content
                ref="customContent"
                :post.sync="editedPost"
              ></custom-content>
            </v-col>
          </v-row>

          <v-row class="mt-4">
            <v-col cols="12" md="5" lg="4">
              <v-btn
                v-text="$t('save')"
                :disabled="!isModelChanged()"
                color="primary"
                class="mr-3"
                tabindex="2"
                outlined
                @click="submit(false)"
              ></v-btn>
              <v-btn
                v-text="$t('socialMediaPost.edit.saveAndPublish')"
                :disabled="!editedPost.channels.length"
                color="primary"
                tabindex="2"
                @click="submit(true)"
              ></v-btn>
            </v-col>
          </v-row>
        </div>
      </v-form>
    </v-card>
  </v-dialog>
</template>


<script>
import eventBus from '@/eventBus'

import { mapGetters, mapActions } from 'vuex'
import { fileType } from '@/constants'

import PBtnIcon from '@/components/PBtnIcon'
import PDateTimePicker from '@/components/PDateTimePicker'
// import PWysiwygEditor from '@/components/PWysiwygEditor'
import PSyncfusionRichTextEditor from '@/components/PSyncfusionRichTextEditor'
import PFileInput from '@/components/PFileInput'
import PFilePreviewArea from '@/components/PFilePreviewArea'
import PSocialMediaChannelSelector from '@/components/PSocialMediaChannelSelector'

import CustomContent from './CustomContent'
import IncludedEventsList from './IncludedEventsList'

import snackbarMixin from '@/mixins/snackbar.mixin'
import objectMixin from '@/mixins/object.mixin'
import dialogMixin from '@/mixins/dialog.mixin'
import validationMixin from '@/mixins/validation.mixin'
import unsavedChangesMixin from '@/mixins/unsavedChanges.mixin'
import socialMediaChannelMixin from '@/mixins/socialMediaChannel.mixin'
import fileMixin from '@/mixins/file.mixin'


export default {
  components: {
    PBtnIcon,
    PDateTimePicker,
    // PWysiwygEditor,
    PSyncfusionRichTextEditor,
    PFileInput,
    PFilePreviewArea,
    PSocialMediaChannelSelector,
    CustomContent,
    IncludedEventsList
  },


  mixins: [
    snackbarMixin,
    objectMixin,
    dialogMixin,
    validationMixin,
    unsavedChangesMixin,
    socialMediaChannelMixin,
    fileMixin
  ],


  props: {
    post: Object
  },


  data () {
    return {
      dialogOpened: false,
      fileType,
      editedPost: null,
      defaultPost: {
        name: '',
        title: '',
        message: '',
        embedMedia: '',
        includeEvents: false,
        featured: false,
        images: [],
        channels: [],
        scheduledPublishTimeUtc: null,
        unPublishTimeUtc: null
      },
      additionalFields: [],
      channelAdditionalFields: {}
    }
  },


  computed: {
    ...mapGetters('socialMedia', ['socialMedias']),
    ...mapGetters('socialMediaChannel', ['socialMediaChannels', 'socialMediaChannelById']),
    ...mapGetters('project', ['currentProjectId']),

    rulesToApply () {
      const rule = this.rule

      return {
        name: [rule.required()],
        unPublishTimeUtc: [rule.dateLaterThen(this.editedPost.scheduledPublishTimeUtc)]
      }
    },

    selectedChannels: {
      get () {
        return this.editedPost.channels?.map(channel => channel.channelId) || []
      },
      set (value) {
        const newChannels = []
        for (const channelId of value) {
          const existingChannel = this.editedPost.channels.find(channel => channel.channelId === channelId)
          if (existingChannel) {
            newChannels.push(existingChannel)
          } else {
            const socialMediaId = this.socialMediaChannelById(channelId).socialMediaId
            newChannels.push({
              channelId,
              socialMediaId,
              customPostContentEnabled: false
            })
          }
        }
        this.editedPost.channels = newChannels.sort((a, b) => (a.channelId > b.channelId) ? 1 : -1)
      }
    }
  },


  methods: {
    ...mapActions('socialMediaPost', ['createSocialMediaPost', 'updateSocialMediaPost', 'publishSocialMediaPost']),
    ...mapActions('file', ['uploadFile']),
    ...mapActions('changes', ['removeFromChanges']),

    initEventBus () {
      eventBus.$on('filesDataUpdated', () => {
        this.editedPost.filesDataUpdated = true
      })
    },

    clearEventBus () {
      delete eventBus.filesDataByGuidDefault
    },

    onCloseClick () {
      this.close()
    },

    onEsc () {
      this.close()
    },

    close () {
      this.clearEventBus()
      this.dialogOpened = false
      this.escape(this.closeDialog)
    },

    onDialogOpen () {
      if (this.post) {
        this.removeFromChanges(this.post)
      }
      this.dialogOpened = true
      const editedPost = this.getObjectCopy(this.post || this.defaultPost)
      if (editedPost.channels?.length) {
        editedPost.channels.forEach(channel => {
          channel.customPostContentEnabled = !!channel.customPostContent
        })
      }
      editedPost.filesDataUpdated = false
      this.$set(this, 'editedPost', editedPost)
      this.initWatchedModel(this.editedPost)
      this.resetValidation()

      if (this.editedPost.channels?.length) {
        this.editedPost.channels.forEach(channel => {
          const wordpressCustomContentName = `wordpressCustomContent${channel.channelId}`
          if (this.$refs?.customContent?.$refs[wordpressCustomContentName]) {
            const channelComponent = this.$refs?.customContent?.$refs[wordpressCustomContentName][0]
            channelComponent?.resetValidation()
            channelComponent?.setContent(this.editedPost)
          }
        })
      }
    },

    async onAddSocialMediaChannel (socialMediaId) {
      const channels = await this.addSocialMediaChannel(socialMediaId)

      for (const channel of channels) {
        this.editedPost.channels.push({
          channelId: channel.id,
          socialMediaId,
          customPostContent: null,
          customPostContentEnabled: false
        })
      }
    },

    async submit (publish) {
      const valid = await this.validate(this.rulesToApply)

      let validChannelsAll = true

      if (this.editedPost.channels?.length) {
        const promisesArray = []
        this.editedPost.channels.forEach(channel => {
          if (channel.customPostContentEnabled) {
            const wordpressCustomContentName = `wordpressCustomContent${channel.channelId}`
            if (this.$refs?.customContent?.$refs[wordpressCustomContentName]) {
              const channelComponent = this.$refs?.customContent?.$refs[wordpressCustomContentName][0]
              promisesArray.push(channelComponent.validate(channelComponent.rulesToApply))
            }
          }
        })
        if (promisesArray.length) {
          const validChannels = await Promise.all(promisesArray)
          validChannelsAll = !validChannels.includes(false)
        }
      }

      if (!(valid && validChannelsAll)) {
        this.scrollToFirstError()
        return
      }

      try {
        this.showSnackbarProcessing()
        delete this.editedPost.filesDataUpdated
        let post = this.getObjectCopy(this.editedPost)

        if (this.isModelChanged()) {
          if (this.editedPost.images?.length) {
            this.additionalFields = []
            this.editedPost.images.forEach(el => {
              const guid = this.getFileGuid(el)
              this.additionalFields.push(this.$refs?.filePreviewArea?.filesDataByGuid[guid])
            })

            this.showSnackbarProcessing(this.$t('socialMediaPost.edit.uploadingImages'))
            await this.$refs.fileInput.uploadFiles(this.currentProjectId, fileType.socialMedia)
            this.hideSnackbar()

            post.images = []
            this.editedPost.images.forEach((el, index) => {
              const preparedImage = {}
              preparedImage.id = el.id
              preparedImage.title = this.additionalFields[index].title
              preparedImage.text = this.additionalFields[index].text
              post.images.push(preparedImage)
            })
          }

          if (this.editedPost.channels?.length) {
            const promisesArray = []
            this.editedPost.channels.forEach(channel => {
              if (channel.customPostContentEnabled && channel.customPostContent?.images?.length) {
                this.channelAdditionalFields[channel.channelId] = []

                channel.customPostContent.images.forEach(el => {
                  const guid = this.getFileGuid(el)
                  const wordpressCustomContentName = `wordpressCustomContent${channel.channelId}`
                  if (this.$refs?.customContent?.$refs[wordpressCustomContentName]) {
                    this.channelAdditionalFields[channel.channelId].push(
                      this.$refs?.customContent?.$refs[wordpressCustomContentName][0].$refs?.filePreviewArea.filesDataByGuid[guid]
                    )
                    promisesArray.push(
                      this.$refs?.customContent?.$refs[wordpressCustomContentName][0].$refs?.fileInput.uploadFiles(this.currentProjectId, fileType.socialMedia)
                    )
                  }
                })
              }
            })

            this.showSnackbarProcessing(this.$t('socialMediaPost.edit.uploadingImagesForChannels'))
            await Promise.all(promisesArray)
            this.hideSnackbar()

            post.channels = this.getObjectCopy(this.editedPost.channels)
            post.channels.forEach(channel => {
              if (channel.customPostContentEnabled) {
                if (channel.customPostContent?.images?.length && this.channelAdditionalFields[channel.channelId] && this.channelAdditionalFields[channel.channelId].length) {
                  const wordpressCustomContentName = `wordpressCustomContent${channel.channelId}`
                  if (this.$refs?.customContent?.$refs[wordpressCustomContentName]) {
                    const preparedImages = []
                    channel.customPostContent.images.forEach((el, index) => {
                      const preparedImage = {}
                      preparedImage.id = el.id
                      preparedImage.title = this.channelAdditionalFields[channel.channelId][index].title
                      preparedImage.text = this.channelAdditionalFields[channel.channelId][index].text
                      preparedImages.push(preparedImage)
                    })
                    channel.customPostContent.images = [...preparedImages]
                  }
                }
              }
            })
          }

          post.channels.forEach(channel => {
            if (!channel.customPostContentEnabled) {
              delete channel.customPostContent
            }
            delete channel.customPostContentEnabled
          })

          if (post.id) {
            post = await this.updateSocialMediaPost(post)
            this.showSnackbarSuccess(this.$t('socialMediaPost.postUpdated'))
          } else {
            post.projectId = this.currentProjectId
            post = await this.createSocialMediaPost(post)
            this.showSnackbarSuccess(this.$t('socialMediaPost.postCreated'))
          }
        }

        if (publish && post.id && post.channels.length) {
          await this.publishSocialMediaPost(post)
          this.showSnackbarSuccess(this.$t('socialMediaPost.postPublished'))
        }
        this.dialogOpened = false
        this.closeDialog()
      } catch (error) {
        this.showSnackbarError()
        this.$log.error(error)
      }
    }
  },

  created () {
    this.initEventBus()
  }
}
</script>
